import { CSSProperties, useEffect, useRef } from "react";
import * as THREE from "three";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { GLTFLoader, GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";

const DEG         = Math.PI / 180;
const GROUND_SIZE = 10;
const HALF        = GROUND_SIZE / 2;
const SHADOW_SIZE = 2048;

export interface OrthoSatelliteRenderOptions {
    glbUrl:            string;
    groundTextureUrl?: string;
    vehicleAzimuth:    number;
    sunAzimuth:        number;
    sunElevation:      number;
    satAzimuth:        number;
    satElevation:      number;
    rotation?:         [number, number, number];
    width:             number;
    height:            number;
    pixelRatio?:       number;
    carPaint:          THREE.MeshPhysicalMaterial;
}

export const carPaints = {
    red: new THREE.MeshPhysicalMaterial({
        color: 0x8b0000,
        metalness: 0.85,
        roughness: 0.25,
        clearcoat: 1.0,
        clearcoatRoughness: 0.05,
    }),
    blue: new THREE.MeshPhysicalMaterial({
        color: 0x0a1e5c,
        metalness: 0.85,
        roughness: 0.25,
        clearcoat: 1.0,
        clearcoatRoughness: 0.05,
    }),
    black: new THREE.MeshPhysicalMaterial({
        color: 0x050505,
        metalness: 0.8,
        roughness: 0.15,  // glassier for that deep black look
        clearcoat: 1.0,
        clearcoatRoughness: 0.03,
    }),
    silver: new THREE.MeshPhysicalMaterial({
        color: 0xc0c0c0,
        metalness: 0.95,
        roughness: 0.3,
        clearcoat: 0.8,
        clearcoatRoughness: 0.1,
    }),
    white: new THREE.MeshPhysicalMaterial({
        color: 0xf0f0f0,
        metalness: 0.1,   // white paint is mostly non-metallic
        roughness: 0.35,
        clearcoat: 1.0,
        clearcoatRoughness: 0.08,
    }),
};

function sunPosition( azimuthDeg: number, elevationDeg: number, dist: number ): THREE.Vector3 {
    const az = azimuthDeg   * DEG;
    const el = elevationDeg * DEG;
    const x =  Math.cos(el) * Math.sin(az);
    const z = -Math.cos(el) * Math.cos(az);   // the only change from the original: sign on z
    const y =  Math.sin(el);
    return new THREE.Vector3( x, y, z ).multiplyScalar( dist );
}

const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader( dracoLoader );

function loadGLTF( url: string ): Promise<GLTF> {
    return new Promise( ( resolve, reject ) => gltfLoader.load( url, resolve, undefined, reject ) );
}

const gltfCache = new Map<string, THREE.Group>();
async function loadGLTFCached(url: string): Promise<THREE.Group> {
    const cached = gltfCache.get( url );
    if( cached ) {
        return cached.clone( true );
    }
    const gltf  = await loadGLTF( url );
    const scene = gltf.scene;
    gltfCache.set( url, scene );    
    const group = await scene.clone( true );
    return group;
}

function loadTexture( url: string ): Promise<THREE.Texture> {
    return new Promise( ( resolve, reject ) => {
        new THREE.TextureLoader().load( url, resolve, undefined, reject );
    } );
}

export function initOrthoSatelliteRenderer(): [HTMLCanvasElement, THREE.WebGLRenderer] {
    const canvas   = document.createElement( "canvas" );
    const renderer = new THREE.WebGLRenderer( {
        canvas,
        antialias: true,
        preserveDrawingBuffer: true,
    } );
    return [canvas, renderer];
}

export async function renderOrthoSatelliteImageBitmap(
    canvas:   HTMLCanvasElement,
    renderer: THREE.WebGLRenderer,
    opts:     OrthoSatelliteRenderOptions,
): Promise<ImageBitmap> {
    const {
        glbUrl,
        groundTextureUrl,
        vehicleAzimuth,
        sunAzimuth,
        sunElevation,
        satAzimuth,
        satElevation,
        rotation,
        width,
        height,
        pixelRatio = 1, //window.devicePixelRatio,
        carPaint
    } = opts;
        
    renderer.setPixelRatio( pixelRatio );
    renderer.setSize( width, height, false );
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    //scene
    const scene      = new THREE.Scene();
    scene.background = new THREE.Color( 0xffffff );

    //scene group
    const sceneGroup = new THREE.Group();
    scene.add( sceneGroup );

    //vehicle group
    const vehicleGroup = new THREE.Group();
    sceneGroup.add( vehicleGroup );

    // const cam = new THREE.OrthographicCamera( -HALF, HALF, HALF, -HALF, 0.1, 1000 );
    const METERS_PER_PIXEL = 0.30;
    const cam = new THREE.OrthographicCamera(
        -( width * METERS_PER_PIXEL ) / 2,
        ( width * METERS_PER_PIXEL ) / 2,
        ( height * METERS_PER_PIXEL ) / 2,
        -( height * METERS_PER_PIXEL ) / 2,
        0.1,
        1000,
    );
    cam.position.set( 0, 100, 0 );
    cam.up.set( 0, 0, -1 );          // keep +z "down" on screen
    cam.lookAt( 0, 0, 0 );
    cam.updateProjectionMatrix();

    const sun = new THREE.DirectionalLight( 0xffffff, 3.0 );
    sun.castShadow = true;
    sun.shadow.mapSize.set( SHADOW_SIZE, SHADOW_SIZE );
    sun.shadow.camera.left = -HALF;
    sun.shadow.camera.right = HALF;
    sun.shadow.camera.top = HALF;
    sun.shadow.camera.bottom = -HALF;
    sun.shadow.camera.near = 0.1;
    sun.shadow.camera.far = 100;
    sun.shadow.bias = -0.0005;
    sun.target.position.set( 0, 0, 0 );

    sceneGroup.add( sun );
    sceneGroup.add( sun.target );
    // scene.add( new THREE.AmbientLight( 0xffffff, 0.35 ) );

    const groundMat = new THREE.MeshStandardMaterial( {
        color: 0x6f6f6f,
        roughness: 1.0,
    } );

    const ground = new THREE.Mesh(
        new THREE.PlaneGeometry( GROUND_SIZE, GROUND_SIZE ),
        groundMat,
    );

    //-180 / 2 = -90
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    sceneGroup.add( ground );

    if( groundTextureUrl ) {
        const tex = await loadTexture( groundTextureUrl );
        tex.colorSpace = THREE.SRGBColorSpace;
        groundMat.map = tex;
        groundMat.color.set( 0xffffff );
        groundMat.needsUpdate = true;
    }

    const model = await loadGLTFCached( glbUrl );

    const blackGlass = new THREE.MeshPhysicalMaterial( {
        color:               0x010101,
        metalness:           0.0,       // glass is dielectric, not metallic
        roughness:           0.02,      // near-mirror smooth
        clearcoat:           1.0,
        clearcoatRoughness:  0.01,
        reflectivity:        1.0,       // max fresnel reflectance
        ior:                 1.8,       // high IOR = stronger reflections at angles
    } );

    model.traverse( ( o: THREE.Object3D ) => {
        if( ( o as THREE.Mesh ).isMesh ) {
            const mesh = o as THREE.Mesh;
            mesh.castShadow = true;
            mesh.receiveShadow = true;

            // --- material swap starts here ---
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            const replaced = mats.map( mat => {
                if( mat instanceof THREE.MeshStandardMaterial || mat instanceof THREE.MeshPhysicalMaterial ) {
                    const { r, g, b } = mat.color;                    
                    const isDark = r < 0.33 && g < 0.33 && b < 0.33;                    
                    if( !isDark ) {
                        return carPaint;
                    }
                    if( isDark ) {
                        return blackGlass;
                    }
                }                
                return mat;
            });
            mesh.material = Array.isArray(mesh.material) ? replaced : replaced[0];
            // --- material swap ends here ---
        }
    } );

    const box = new THREE.Box3().setFromObject( model );
    const center = box.getCenter( new THREE.Vector3() );
    model.position.x -= center.x;
    model.position.z -= center.z;
    model.position.y -= box.min.y;

    vehicleGroup.add( model );
    sun.position.copy( sunPosition( sunAzimuth, sunElevation, 50 ) );
    const [, , rz] = rotation ?? [0, 0, 0];

    sceneGroup.rotation.set(
        ( 90 - satElevation ) * DEG,
        -satAzimuth * DEG,
        rz * DEG,
    );

    vehicleGroup.rotation.y = -vehicleAzimuth * DEG;
    renderer.render( scene, cam );
    const bitmap = await createImageBitmap( canvas );
    ground.geometry.dispose();
    groundMat.map?.dispose();
    groundMat.dispose();
    sun.shadow?.map?.dispose();
    return bitmap;
}

export async function rotateSatelliteBitmap(
    source:           ImageBitmap,
    satelliteAzimuth: number,
    outputWidth  = source.width,
    outputHeight = source.height,
): Promise<ImageBitmap> {
    const angle  = -satelliteAzimuth * Math.PI / 180;
    const canvas = new OffscreenCanvas( outputWidth, outputHeight );
    const ctx    = canvas.getContext( "2d" );

    if( !ctx ) {
        throw new Error( "Could not create OffscreenCanvas 2D context" );
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.clearRect( 0, 0, outputWidth, outputHeight );
    ctx.translate( outputWidth / 2, outputHeight / 2 );
    ctx.rotate( angle );
    ctx.drawImage(
        source,
        -source.width / 2,
        -source.height / 2,
        source.width,
        source.height,
    );
    return canvas.transferToImageBitmap();
}

export interface OrthoSatelliteBitmapViewProps {
    glbUrl: string;
    groundTextureUrl?: string;
    vehicleAzimuth: number;
    sunAzimuth: number;
    sunElevation: number;
    satAzimuth: number;
    satElevation: number;
    rotation?: [number, number, number];
    width?: number;
    height?: number;
    renderScale?: number;
    style?: CSSProperties;
    className?: string;
}

// export function OrthoSatelliteView( {
//     glbUrl,
//     groundTextureUrl,
//     vehicleAzimuth,
//     sunAzimuth,
//     sunElevation,
//     satAzimuth,
//     satElevation,
//     rotation,
//     width = 1024,
//     height = 1024,
//     renderScale = 1,
//     style,
//     className,
// }: OrthoSatelliteBitmapViewProps ) {
//     const canvasRef   = useRef<HTMLCanvasElement | null>( null );
//     const rendererRef = useRef<[HTMLCanvasElement, THREE.WebGLRenderer] | null>( null );
//     function getRenderer() {
//         if( !rendererRef.current ) {
//             rendererRef.current = initOrthoSatelliteRender();
//         }
//         return rendererRef.current;
//     }

//     useEffect( () => {
//         const canvas = canvasRef.current;
//         if( !canvas ) { return; }
//         let cancelled = false;

//         async function render() {
//             if( !canvas ) return;
//             const renderWidth = width * renderScale;
//             const renderHeight = height * renderScale;
//             const bitmap = await renderOrthoSatelliteImageBitmap( {
//                 glbUrl,
//                 groundTextureUrl,
//                 vehicleAzimuth,
//                 sunAzimuth,
//                 sunElevation,
//                 satAzimuth,
//                 satElevation,
//                 rotation,
//                 width: renderWidth,
//                 height: renderHeight,
//             } );

//             const rotated = await rotateSatelliteBitmap(
//                 bitmap,
//                 satAzimuth,
//                 renderWidth,
//                 renderHeight,
//             );

//             bitmap.close();
//             if( cancelled ) {
//                 rotated.close();
//                 return;
//             }

//             canvas.width  = width;
//             canvas.height = height;
//             const ctx = canvas.getContext( "2d" );
//             if( !ctx ) {
//                 rotated.close();
//                 return;
//             }

//             ctx.imageSmoothingEnabled = true;
//             ctx.imageSmoothingQuality = "high";
//             ctx.clearRect( 0, 0, width, height );
//             ctx.drawImage( rotated, 0, 0, width, height );
//             rotated.close();
//         }
//         render().catch( console.error );
//         return () => {
//             cancelled = true;
//         };
//     }, [
//         glbUrl,
//         groundTextureUrl,
//         vehicleAzimuth,
//         sunAzimuth,
//         sunElevation,
//         satAzimuth,
//         satElevation,
//         rotation,
//         width,
//         height,
//         renderScale,
//     ] );

//     return (
//         <canvas
//             ref={canvasRef}
//             className={className}
//             style={{
//                 width,
//                 height,
//                 display: "block",
//                 ...style,
//             }}
//         />
//     );
// }

export async function bitmapToObjectUrl( bitmap: ImageBitmap ): Promise<string> {
    const canvas = new OffscreenCanvas( bitmap.width, bitmap.height );
    const ctx = canvas.getContext( "2d" );
    if( !ctx ) {
        throw new Error( "Could not create OffscreenCanvas 2D context" );
    }
    ctx.drawImage( bitmap, 0, 0 );
    const blob = await canvas.convertToBlob( { type: "image/png" } );
    return URL.createObjectURL( blob );
}
